/**
 * Tâche 1313 (Eric 2026-06-30) — Alerte « enregistrement saturé » dès l'import.
 *
 * La mesure se fait côté serveur en tâche de fond, quelques secondes après la
 * fin du transfert : on interroge donc jusqu'à ce qu'elle arrive, sans jamais
 * conclure « tout va bien » tant qu'elle est en attente.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Waveform } from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

const INTERVALLE_MS = 5000;
const TENTATIVES_MAX = 36; // 3 minutes : au-delà, la mesure ne viendra plus

export const AudioSaturationAlert = ({ videoId }) => {
  const { tr } = useLang();
  const [etat, setEtat] = useState(null);
  const tentatives = useRef(0);

  useEffect(() => {
    if (!videoId) return undefined;
    let vivant = true;
    tentatives.current = 0;
    const demander = async () => {
      if (!vivant) return;
      try {
        const { data } = await axios.get(`${API}/api/videos/${videoId}/audio-check`, {
          withCredentials: true,
          timeout: 10000,
        });
        if (!vivant) return;
        if (data?.status === "pending") {
          tentatives.current += 1;
          if (tentatives.current < TENTATIVES_MAX) setTimeout(demander, INTERVALLE_MS);
          return;
        }
        setEtat(data);
      } catch (_) {
        // Une mesure de confort ne doit jamais faire crier la page d'import.
      }
    };
    demander();
    return () => { vivant = false; };
  }, [videoId]);

  if (etat?.status !== "saturated") return null;

  return (
    <div
      className="mb-6 text-left rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
      data-testid="audio-saturation-alert"
      title={etat.detail || ""}
    >
      <div className="flex items-start gap-3">
        <Waveform size={18} weight="bold" className="text-amber-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900" data-testid="audio-saturation-title">
            {tr("Enregistrement saturé", "Clipped recording")}
          </p>
          <p className="text-sm text-amber-900/90 mt-1">
            {tr(
              "Le micro écrête : la transcription risque d'inventer des mots et le découpage des locuteurs devient peu fiable. Rien n'est réparable après coup — baissez le gain du micro et refaites la prise si la qualité compte.",
              "The microphone is clipping: the transcript may invent words and the speaker split becomes unreliable. Nothing can be repaired afterwards — lower the microphone gain and record again if quality matters.",
            )}
          </p>
          {etat.detail ? (
            <p
              className="text-[11px] text-amber-800/70 mt-2 cursor-help"
              data-testid="audio-saturation-detail"
              title={etat.detail}
            >
              {tr("Niveaux mesurés au survol", "Measured levels on hover")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AudioSaturationAlert;
